import { Avatar, Box, FlexBox, IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { User } from 'lib-shared-types-api';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { DeepWritable } from 'ts-essentials';

import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import {
  ConversationMessageEventBox,
  variantConfig,
} from '../messageEventBox/conversationMessageEventBox';
import { ConversationMessageEventBoxVariant } from '../messageEventBox/conversationMessageEventBox.types';

import styles from './conversationTextualMessage.module.scss';
import { ConversationTextualMediaMessageEvents } from './subComponents/textualMediaMessageEvents/conversationTextualMediaMessageEvents';

/**
 * Represents the maximum height of the content observer element. This height
 * determines the point at which the scroll to top button becomes visible.
 */
const contentObserverMaxHeight = 300;

/**
 * Represents the minimum height of the content element used for computing
 * whether to show the scroll to top button and whether to make the header
 * elements in the message sticky (when enabled). This number should not exceed
 * that of {@link contentObserverMaxHeight}.
 */
const contentObservedMinHeight = 160;

type ConversationTextualMessageProps = PropsWithChildren &
  PropsWithClassName & {
    /** The variant of the message. */
    variant: Exclude<ConversationMessageEventBoxVariant, 'audio'>;
    /** The author of the message. */
    author?: User;
    /** The timestamp of the message. */
    timestamp?: string;
    /** The slot for projected content after the author name of the message. */
    authorEndSlot?: ReactNode;
    /** The slot for projected content for the author's description. */
    authorDescriptionSlot?: ReactNode;
    /**
     * The additional gutter actions.
     *
     * For correct presentation the action should be an {@link IconButton}
     */
    gutterActionSlot?: ReactNode;
    /** Whether to hide the gutter actions. */
    hideGutterActions?: boolean;
    /**
     * Whether the header elements of the message, such as the avatar, the
     * author and the gutter, should stick to the top while scrolling through
     * long content.
     */
    stickyHeader?: boolean;
  };

/** Represents the component for displaying a conversation textual message. */
export const ConversationTextualMessage = ({
  author,
  authorEndSlot,
  timestamp,
  children,
  variant,
  className,
  gutterActionSlot,
  hideGutterActions,
  authorDescriptionSlot,
  stickyHeader = false,
}: ConversationTextualMessageProps): JSX.Element => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<JSX.IntrinsicElements['div']>(null);
  const observerRef = useRef<HTMLDivElement>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [hasTallContent, setHasTallContent] = useState(false);

  const isStickyHeader = stickyHeader && hasTallContent;

  const authorDisplayName = useMemo(
    () => (author ? getUserDisplayName(author) : 'Unknown'),
    [author],
  );

  // Handles determining whether the content of the message is tall enough for
  // the header elements to be sticky and the scroll to top button to be shown.
  useEffect(() => {
    const currentRoot = contentRef.current as Element;

    const resizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        entries.forEach((entry) => {
          if (currentRoot && entry.target === currentRoot) {
            setHasTallContent(
              entry.contentRect.height > contentObservedMinHeight,
            );
          }
        });
      },
    );

    if (currentRoot) {
      resizeObserver.observe(currentRoot);
    }

    return () => {
      if (currentRoot) {
        resizeObserver.unobserve(currentRoot);
      }
    };
  }, []);

  // Handles determining whether to show the scroll to top button.
  useEffect(() => {
    if (!hasTallContent) {
      setShowScrollToTop(false);
      return;
    }

    const observerElement = observerRef.current;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (observerElement && entry.target === observerElement) {
            setShowScrollToTop(
              entry.boundingClientRect.height > contentObservedMinHeight &&
                !entry.isIntersecting,
            );
          }
        });
      },
      { threshold: [0, 1] },
    );

    if (observerElement) {
      intersectionObserver.observe(observerElement);
    }

    return () => {
      if (observerElement) {
        intersectionObserver.unobserve(observerElement);
      }

      intersectionObserver.disconnect();
    };
  }, [hasTallContent]);

  return (
    <div ref={rootRef} className={className}>
      <ConversationMessageEventBox variant={variant}>
        <FlexBox gap="250">
          <Avatar
            className={classnames(styles.avatar, {
              [styles.isSticky]: isStickyHeader,
            })}
            user={author as DeepWritable<User>}
          />

          <FlexBox flex="1" flexDirection="column" minWidth="0">
            <Box
              className={classnames(styles.authorContainer, {
                [styles.isSticky]: isStickyHeader,
              })}
              style={{
                ...(isStickyHeader
                  ? {
                      backgroundImage: `linear-gradient(to bottom, ${variantConfig[variant].background} 80%, transparent)`,
                    }
                  : {}),
              }}
            >
              <FlexBox gap="200">
                <strong>{authorDisplayName}</strong>
                {authorEndSlot && <Box flex="1">{authorEndSlot}</Box>}
              </FlexBox>

              {authorDescriptionSlot && <Box>{authorDescriptionSlot}</Box>}
            </Box>

            <Box ref={contentRef} className={styles.contentContainer}>
              <div
                ref={observerRef}
                className={styles.contentObserver}
                style={{ maxHeight: contentObserverMaxHeight }}
              />
              <div className={styles.content}>{children}</div>
            </Box>
          </FlexBox>

          <FlexBox
            alignItems="flex-end"
            alignSelf="start"
            className={classnames(styles.gutter, {
              [styles.isSticky]: isStickyHeader,
            })}
            flexDirection="column"
            gap="300"
          >
            {timestamp && (
              <ConversationTimestamp
                shouldDateFormatPersist={true}
                className={styles.timestamp}
                time={timestamp}
              />
            )}

            <FlexBox gap="100" justifyContent="end">
              {!hideGutterActions && gutterActionSlot}

              {showScrollToTop && (
                <IconButton
                  icon="arrow-up"
                  variant="secondary"
                  onClick={() =>
                    rootRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                      inline: 'nearest',
                    })
                  }
                />
              )}
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </ConversationMessageEventBox>
    </div>
  );
};

ConversationTextualMessage.Events = ConversationTextualMediaMessageEvents;
